import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {PrlTranslateService} from '../../../../shared/services/prl-translate.service';
import {PrlAccountDataService} from '../../../prl-dashboard/services/account/prl-account-data.service';
import {AccountDetailObj, IdNameMxObj, MxButtonModel, MxNotificationService,} from 'mx-core';
import {ActivatedRoute} from '@angular/router';
import {PrlPanelComponent} from '../../../../shared/components/prl-panel/prl-panel.component';
import {PrlValidationManagerService} from '../../../../shared/services/prl-validation-manager.service';
import {PrlApplicationPropertiesDataService} from '../../../../shared/services/prl-application-properties.service';
import {combineLatest} from 'rxjs';

@Component({
    selector: 'prl-account',
    templateUrl: './account.component.html',
    standalone: false,
    styleUrls: ['./account.component.scss'],
})
export class AccountComponent implements OnInit, AfterViewInit {

    @ViewChild('contacts') contacts!: PrlPanelComponent;
    @ViewChild('documents') documents!: PrlPanelComponent;

    DIGITS_ONLY_STARTING_NOT_WITH_ZERO = '';
    URL = '';
    vatPayerEnabled = false;
    showSendPromotions = false;
    showDocumentation = false;

    accountForm: FormGroup;

    updateBtn: MxButtonModel;
    addWebsiteBtn: MxButtonModel;
    deleteWebsiteBtn: MxButtonModel;

    constructor(
        private readonly fb: FormBuilder,
        private readonly languageService: PrlLanguageService,
        private readonly dataService: PrlAccountDataService,
        protected readonly translateService: PrlTranslateService,
        private readonly notificationService: MxNotificationService,
        private readonly route: ActivatedRoute,
        private readonly validationService: PrlValidationManagerService,
        protected readonly propertyService: PrlApplicationPropertiesDataService
    ) {
        this.accountForm = this.fb.nonNullable.group({
            username: [{value: '', disabled: true}],
            userLanguageCode: 'EN',
            company: '',
            companyId: '',
            productOfInterest: {value: '', disabled: true},
            messagesEmailNotifier: false,
            sendPromotion: false,
            vatPayer: {value: false, disabled: true},
            taxId: {value: '', disabled: true},
            websites: this.fb.array([])
        });
    }

    ngOnInit() {
        this.DIGITS_ONLY_STARTING_NOT_WITH_ZERO = this.validationService.getRegex('DIGITS_ONLY_STARTING_NOT_WITH_ZERO')?.regex;
        this.URL = this.validationService.getRegex('URL')?.regex;
        this.updateBtn = {
            label: () => this.translateService.getTranslatedLabel('label.submit'),
            tooltip: () => this.translateService.getTranslatedLabel('label.submit'),
            disabled: false,
            className: 'mxs-button--secondary',
            clickHandler: this.updateAccountData
        };

        this.addWebsiteBtn = {
            icon: 'add',
            disabled: false,
            className: 'mxs-button--round  mxs-button--secondary',
            clickHandler: this.addWebsite
        };

        this.deleteWebsiteBtn = {
            icon: 'delete',
            disabled: false,
            className: 'mxs-button--round  mxs-button--secondary',
        };

        this.dataService.getAccountDetails().subscribe((data: AccountDetailObj) => {
            this.accountForm.patchValue({
                    username: data.userName,
                    userLanguageCode: data.languageCode,
                    company: data.company,
                    companyId: data.companyId,
                    productOfInterest: data.productOfInterestNames,
                    messagesEmailNotifier: data.messageEmailNotifier,
                    vatPayer: data.vatPayer,
                    taxId: data.taxId,
                    sendPromotion: data.sendPromotion,
                }
            );

            if (data.websites.length < 1) {
                this.addWebsite(); // if affiliate hasn't websites add empty field for website
            } else {
                data.websites.forEach(website => this.addWebsite(website.id, website.name));
            }

            this.updateCompanyValidators(!!data.company?.trim());
        });

        combineLatest({
            vatPayerEnabled: this.propertyService.getBooleanProperty('is-enable-vat-payer'),
            showDocumentation: this.propertyService.getBooleanProperty('show-documentation'),
            showSendPromotions: this.propertyService.showSendPromotions()
        }).subscribe(({vatPayerEnabled, showDocumentation, showSendPromotions}) => {
            this.vatPayerEnabled = vatPayerEnabled;
            this.showDocumentation = showDocumentation;
            this.showSendPromotions = showSendPromotions;
        });

        const company = this.accountForm.get('company');
        company.valueChanges.subscribe(value => {
            this.updateCompanyValidators(!!(value as string)?.trim());
        });
    }

    ngAfterViewInit() {
        this.route.fragment.subscribe(fragment => {
            if (fragment === 'contacts') {
                this.contacts.panel.open();
                document.getElementById('contacts')?.scrollIntoView({behavior: 'smooth'});
            }
            if (fragment === 'documents') {
                this.documents.panel.open();
                document.getElementById('documents')?.scrollIntoView({behavior: 'smooth'});
            }
        });
    }

    languages = () => this.languageService.getAllLanguageList();

    websites() {
        return this.accountForm.get('websites') as FormArray;
    }

    deleteWebsite(index: number) {
        this.websites().removeAt(index);
    }

    addWebsite = (id = '', name = '') => {
        this.websites().push(
            this.fb.group({
                id: this.fb.control(id),
                name: this.fb.control(name, [
                    Validators.pattern(this.URL),
                    this.websiteDuplicateValidator()]),
            })
        );
    };


    onWebsiteNameChange(index: number, name: string) {
        this.websites().at(index).patchValue({name});
    }


    websiteDuplicateValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control.value as string)?.trim().toLowerCase();

            if (!value) {
                return null;
            }

            const group = control.parent;
            if (!group) {
                return null;
            }

            const array = group.parent as FormArray;
            if (!array) {
                return null;
            }

            const currentIndex = array.controls.indexOf(group);

            // Check on duplicate, without element himself
            const isDuplicate = array.controls.some((w, i) => {
                if (i === currentIndex) {
                    return false;
                }
                const other = w.get('name')?.value?.trim().toLowerCase();
                return other === value;
            });

            return isDuplicate ? {duplicate: true} : null;
        };

    }

    updateAccountData = () => {
        if (this.accountForm.invalid) {
            this.accountForm.markAllAsTouched();
            return;
        }

        const idNameMxObjs: IdNameMxObj[] = this.websites().controls.map(control => {
            const nameMxObj = new IdNameMxObj();
            const id = control.get('id')?.value;
            const name = control.get('name')?.value;

            nameMxObj.id = id;
            nameMxObj.name = name;

            return nameMxObj;
        });

        const obj: AccountDetailObj = {
            userName: this.accountForm.getRawValue().username,
            languageCode: this.accountForm.value.userLanguageCode,
            company: this.accountForm.value.company,
            companyId: this.accountForm.value.companyId,
            productOfInterestNames: this.accountForm.getRawValue().productOfInterest,
            messageEmailNotifier: this.accountForm.value.messagesEmailNotifier,
            sendPromotion: this.accountForm.value.sendPromotion,
            vatPayer: this.accountForm.getRawValue().vatPayer,
            taxId: this.accountForm.getRawValue().taxId,
            websites: idNameMxObjs
        };

        this.dataService.updateAccountData(obj).subscribe((response) => {
            if (response.status === 204) {
                this.notificationService.showSuccess('Account Details successfully updated');
            }
        });
    };

    private updateCompanyValidators(companyHasValue: boolean): void {
        const company = this.accountForm.get('company');
        const companyId = this.accountForm.get('companyId');

        company.setValidators([Validators.maxLength(50)]);

        companyId.setValidators(companyHasValue
            ? [
                Validators.required,
                Validators.pattern(this.DIGITS_ONLY_STARTING_NOT_WITH_ZERO),
                Validators.minLength(5),
                Validators.maxLength(15)
            ]
            : [
                Validators.pattern(this.DIGITS_ONLY_STARTING_NOT_WITH_ZERO),
                Validators.minLength(5),
                Validators.maxLength(15)
            ]);

        company.updateValueAndValidity({emitEvent: false});
        companyId.updateValueAndValidity({emitEvent: false});
    }
}
